import { useRef, useState } from 'react';
import { Camera, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from './AvatarWithInitials';
import { uploadToCloudinary } from '@/lib/cloudinary';

/**
 * Circular photo preview + upload button. Uploads directly to Cloudinary
 * (unsigned preset) and calls onChange(secure_url) on success — the
 * employee record only ever stores the resulting URL, never the file.
 */
export function PhotoUpload({ name, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      onChange(url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Avatar name={name} src={value} size="xl" />
        {uploading && (
          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
            <Loader2 size={18} className="text-white animate-spin" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1.5 h-8 px-3 rounded-[8px] border border-[var(--border)] text-[12px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-muted)] transition-colors disabled:opacity-50">
          <Camera size={13} /> {value ? 'Change Photo' : 'Upload Photo'}
        </button>
        {value && (
          <button type="button" onClick={() => onChange(null)}
            className="flex items-center gap-1 text-[11px] text-[var(--text-tertiary)] hover:text-[var(--danger)] transition-colors">
            <X size={11} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}
